import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";
import type { UserProfile, LinkedDocument } from "@/domains/pm/types";
import type { ProjectMessage, ReactionSummary, ReplySnippet } from "@/domains/messages/types";
import { attachDocumentBreadcrumbs } from "@/stores/pm/repository";

// The exported client is untyped, and several message-board tables are missing
// from the generated Database types, so the whole module goes through one
// untyped SupabaseClient view instead of casting the client at each call site.
const db: SupabaseClient = supabase;

// All Supabase access for the project message board lives here. The MST
// MessagesStore wraps these in flow() actions; components never call supabase
// directly. Patterns (no-FK author/document hydration, best-effort junction
// linking, postgres_changes channels) mirror src/stores/pm/repository.ts.

const MESSAGE_COLUMNS =
  "id, building_project_id, org_id, building_id, author_id, body, reply_to_id, created_at, modified_at, modified_by, deleted_at";

const DOCUMENT_COLUMNS =
  "id, filename, state, content_type, file_path, size, created_at, parent_document_id, version, project_id, folder_id";

const REPLY_SNIPPET_MAX = 140;

function authorDisplayName(user: UserProfile | undefined): string {
  if (!user) return "Unknown";
  const name = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return name || user.email;
}

/**
 * Load a channel's full message feed (oldest-first), hydrating authors,
 * attachments, reactions, and reply snippets. Soft-deleted messages ARE
 * included (they render as a "Deleted message" tombstone); their reply
 * snippets are flagged `deleted`. The parent RLS policy still returns them, so
 * we don't filter deleted_at here.
 */
export async function listMessages(
  buildingProjectId: number,
  currentUserId: number
): Promise<ProjectMessage[]> {
  const { data, error } = await db
    .from("project_messages")
    .select(MESSAGE_COLUMNS)
    .eq("building_project_id", buildingProjectId)
    .order("created_at", { ascending: true });

  if (error) throw new Error(error.message);

  const messages = ((data ?? []) as ProjectMessage[]).map((m) => ({
    ...m,
    linked_documents: [] as LinkedDocument[],
    reactions: [] as ReactionSummary[],
  }));
  if (messages.length === 0) return messages;

  const messageIds = messages.map((m) => m.id);

  // Author profiles — no FK embed exists between project_messages and
  // user_profiles, so batch-resolve in one round-trip.
  const authorIds = Array.from(new Set(messages.map((m) => m.author_id)));
  const { data: users, error: uErr } = await db
    .from("user_profiles")
    .select("id, first_name, last_name, email, avatar_url, color")
    .in("id", authorIds);
  if (uErr) throw new Error(uErr.message);
  const userMap = new Map<number, UserProfile>((users ?? []).map((u: UserProfile) => [u.id, u]));
  for (const m of messages) m.author = userMap.get(m.author_id);

  await hydrateAttachments(messages, messageIds);
  await hydrateReactions(messages, messageIds, currentUserId);
  hydrateReplySnippets(messages);

  return messages;
}

/** Attach documents linked via the project_message_documents junction. */
async function hydrateAttachments(messages: ProjectMessage[], messageIds: number[]): Promise<void> {
  const { data: linkRows, error: linkErr } = await db
    .from("project_message_documents")
    .select("message_id, document_id")
    .in("message_id", messageIds);
  if (linkErr) throw new Error(linkErr.message);

  const rows = (linkRows ?? []) as { message_id: number; document_id: number }[];
  const docIds = Array.from(new Set(rows.map((r) => r.document_id)));
  if (docIds.length === 0) return;

  const { data: docs, error: docErr } = await db
    .from("documents")
    .select(DOCUMENT_COLUMNS)
    .in("id", docIds);
  if (docErr) throw new Error(docErr.message);

  const docMap = new Map<number, LinkedDocument>(
    ((docs ?? []) as LinkedDocument[]).map((d) => [d.id, d])
  );
  // Resolve interactive folder-path breadcrumbs for the hover tooltip, reusing
  // the same resolver the /documents page + PM comments use (WHA-3457).
  await attachDocumentBreadcrumbs(Array.from(docMap.values()));
  const grouped = new Map<number, LinkedDocument[]>();
  for (const r of rows) {
    const doc = docMap.get(r.document_id);
    if (!doc) continue;
    const list = grouped.get(r.message_id) ?? [];
    list.push(doc);
    grouped.set(r.message_id, list);
  }
  for (const m of messages) m.linked_documents = grouped.get(m.id) ?? [];
}

/** Aggregate project_message_reactions into per-message ReactionSummary[]. */
async function hydrateReactions(
  messages: ProjectMessage[],
  messageIds: number[],
  currentUserId: number
): Promise<void> {
  const { data: reactionRows, error: rErr } = await db
    .from("project_message_reactions")
    .select("message_id, user_id, emoji")
    .in("message_id", messageIds);
  if (rErr) throw new Error(rErr.message);

  const rows = (reactionRows ?? []) as { message_id: number; user_id: number; emoji: string }[];
  // message_id -> emoji -> summary
  const byMessage = new Map<number, Map<string, ReactionSummary>>();
  for (const r of rows) {
    let emojiMap = byMessage.get(r.message_id);
    if (!emojiMap) {
      emojiMap = new Map();
      byMessage.set(r.message_id, emojiMap);
    }
    const summary = emojiMap.get(r.emoji) ?? { emoji: r.emoji, count: 0, reactedByMe: false };
    summary.count += 1;
    if (r.user_id === currentUserId) summary.reactedByMe = true;
    emojiMap.set(r.emoji, summary);
  }
  for (const m of messages) {
    const emojiMap = byMessage.get(m.id);
    m.reactions = emojiMap ? Array.from(emojiMap.values()) : [];
  }
}

/** Resolve each reply's quoted snippet from the loaded message set. */
function hydrateReplySnippets(messages: ProjectMessage[]): void {
  const byId = new Map<number, ProjectMessage>(messages.map((m) => [m.id, m]));
  for (const m of messages) {
    if (m.reply_to_id == null) {
      m.reply_to = null;
      continue;
    }
    const parent = byId.get(m.reply_to_id);
    if (!parent || parent.deleted_at) {
      // Parent deleted or outside this user's access.
      m.reply_to = { id: m.reply_to_id, authorName: "", body: "", deleted: true };
      continue;
    }
    const body =
      parent.body.length > REPLY_SNIPPET_MAX
        ? `${parent.body.slice(0, REPLY_SNIPPET_MAX)}…`
        : parent.body;
    const snippet: ReplySnippet = {
      id: parent.id,
      authorName: authorDisplayName(parent.author),
      body,
      deleted: false,
    };
    m.reply_to = snippet;
  }
}

export async function createMessage(
  messageData: {
    building_project_id: number;
    author_id: number;
    body: string;
    reply_to_id: number | null;
  },
  mentionedUserIds: number[] = [],
  documentIds: number[] = []
): Promise<ProjectMessage> {
  const { data, error } = await db
    .from("project_messages")
    .insert(messageData)
    .select(MESSAGE_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  const message = data as ProjectMessage;

  // Best-effort attachment + mention linking — a failure here logs but does
  // not roll back the message, matching the PM comment plumbing.
  if (documentIds.length > 0) {
    try {
      const rows = documentIds.map((documentId) => ({
        message_id: message.id,
        document_id: documentId,
      }));
      const { error: docErr } = await db.from("project_message_documents").insert(rows);
      if (docErr) throw docErr;
    } catch (docErr) {
      console.error("[messagesRepo.createMessage] document linking failed:", docErr);
    }
  }

  if (mentionedUserIds.length > 0) {
    try {
      const rows = mentionedUserIds.map((userId) => ({
        message_id: message.id,
        mentioned_user_id: userId,
      }));
      const { error: mentionErr } = await db.from("project_message_mentions").insert(rows);
      if (mentionErr) throw mentionErr;
    } catch (mentionErr) {
      console.error("[messagesRepo.createMessage] mention plumbing failed:", mentionErr);
    }
  }

  return message;
}

/**
 * Fire the project message-board email notification for a just-posted message.
 * Best-effort and non-blocking: a failed email must never break posting, so the
 * caller does not await this and we swallow errors here (mirrors the org-invite
 * flow, which invokes an Edge Function via supabase.functions.invoke). The
 * function itself resolves recipients, applies opt-out + coalescing, and only
 * the message author may trigger it.
 */
export async function notifyProjectMessage(messageId: number): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke("notify-project-message", {
      body: { messageId },
    });
    if (error) throw error;
  } catch (e) {
    console.error("[messagesRepo.notifyProjectMessage] notification invoke failed:", e);
  }
}

/** Edit a message body; the DB trigger stamps modified_at/modified_by. Mention
 *  rows are resynced to match the edited text (mirrors updateComment). */
export async function updateMessage(
  messageId: number,
  body: string,
  mentionedUserIds: number[] = []
): Promise<void> {
  const { error } = await db.from("project_messages").update({ body }).eq("id", messageId);
  if (error) throw new Error(error.message);

  try {
    const { error: delErr } = await db
      .from("project_message_mentions")
      .delete()
      .eq("message_id", messageId);
    if (delErr) throw delErr;
    if (mentionedUserIds.length > 0) {
      const rows = mentionedUserIds.map((userId) => ({
        message_id: messageId,
        mentioned_user_id: userId,
      }));
      const { error: insErr } = await db.from("project_message_mentions").insert(rows);
      if (insErr) throw insErr;
    }
  } catch (mentionErr) {
    console.error("[messagesRepo.updateMessage] mention resync failed:", mentionErr);
  }
}

export async function softDeleteMessage(messageId: number): Promise<void> {
  // Clear the body too so the deleted content isn't sent to clients — the feed
  // only shows a "Deleted message" tombstone.
  const { error } = await db
    .from("project_messages")
    .update({ deleted_at: new Date().toISOString(), body: "" })
    .eq("id", messageId);
  if (error) throw new Error(error.message);
}

/**
 * Toggle the current user's reaction for one emoji on a message. Conflict-safe:
 * deletes the row when present, inserts when absent — the PK
 * (message_id, user_id, emoji) makes a double-add a no-op rather than an error.
 */
export async function toggleReaction(
  messageId: number,
  userId: number,
  emoji: string
): Promise<void> {
  const { data: existing, error: selErr } = await db
    .from("project_message_reactions")
    .select("message_id")
    .eq("message_id", messageId)
    .eq("user_id", userId)
    .eq("emoji", emoji)
    .maybeSingle();
  if (selErr) throw new Error(selErr.message);

  if (existing) {
    const { error: delErr } = await db
      .from("project_message_reactions")
      .delete()
      .eq("message_id", messageId)
      .eq("user_id", userId)
      .eq("emoji", emoji);
    if (delErr) throw new Error(delErr.message);
  } else {
    const { error: insErr } = await db
      .from("project_message_reactions")
      .insert({ message_id: messageId, user_id: userId, emoji });
    if (insErr) throw new Error(insErr.message);
  }
}

/**
 * Count the user's unread @-mentions across a building's message channels
 * (optionally narrowed to one channel). Backs the red "N new" badge on the
 * dashboard's MESSAGE BOARD label row. "Unread" is mention-based — the only
 * per-user read state the message board tracks (project_message_mentions.read_at).
 */
export async function getUnreadMentionCount(
  buildingId: number,
  currentUserId: number,
  buildingProjectId?: number | null
): Promise<number> {
  if (!currentUserId) return 0;
  // Single joined count — an id-list round trip through project_messages
  // would silently truncate at PostgREST's max-rows on busy buildings.
  let query = db
    .from("project_message_mentions")
    .select("message_id, project_messages!inner(id)", { count: "exact", head: true })
    .eq("mentioned_user_id", currentUserId)
    .is("read_at", null)
    .eq("project_messages.building_id", buildingId)
    .is("project_messages.deleted_at", null);
  if (buildingProjectId != null) {
    query = query.eq("project_messages.building_project_id", buildingProjectId);
  }
  const { count, error } = await query;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/** Mark every unread @-mention on this channel's messages read for the user. */
export async function markChannelMentionsRead(
  buildingProjectId: number,
  currentUserId: number
): Promise<void> {
  if (!currentUserId) return;
  const { data: rows, error: mErr } = await db
    .from("project_messages")
    .select("id")
    .eq("building_project_id", buildingProjectId)
    .is("deleted_at", null);
  if (mErr) throw new Error(mErr.message);
  const ids = (rows ?? []).map((r: { id: number }) => r.id);
  if (ids.length === 0) return;

  const { error: updErr } = await db
    .from("project_message_mentions")
    .update({ read_at: new Date().toISOString() })
    .in("message_id", ids)
    .eq("mentioned_user_id", currentUserId)
    .is("read_at", null);
  if (updErr) throw new Error(updErr.message);
}

/** The building's default channel: the is_default project, else the oldest. */
export async function getDefaultProjectId(buildingId: number): Promise<number | null> {
  const { data, error } = await db
    .from("building_projects")
    .select("id, is_default, created_at")
    .eq("building_id", buildingId)
    .eq("hidden", false)
    .eq("archived", false)
    .order("is_default", { ascending: false })
    .order("created_at", { ascending: true })
    .limit(1);
  if (error) throw new Error(error.message);
  const row = (data ?? [])[0] as { id: number } | undefined;
  return row?.id ?? null;
}

export function subscribeToMessages(
  buildingProjectId: number,
  callback: () => void
): RealtimeChannel {
  return supabase
    .channel(`project_messages_${buildingProjectId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "project_messages",
        filter: `building_project_id=eq.${buildingProjectId}`,
      },
      callback
    )
    .subscribe();
}

/**
 * Building-wide subscription that keeps the unread-mention badge live while
 * the dashboard widget is mounted: a new message in ANY of the building's
 * channels (not just the selected one) triggers a recount. The count query
 * itself does the scoping, so the callback is just "refresh the badge".
 *
 * ⚠️ The `project_message_mentions` binding below is DEAD: that table is not in
 * the `supabase_realtime` publication, so it never delivers an event. The
 * migration that added the message board says so outright — mentions were
 * expected to refetch when their parent message event fires. That covers a new
 * mention, but NOT a mark-read from another tab or device, which only ever
 * touches `project_message_mentions`. Callers get that case from focus
 * revalidation instead; publishing the table would need a
 * franklin-data-pipeline migration. The binding is kept so it starts working
 * the moment that lands.
 */
export function subscribeToMentionBadge(
  buildingId: number,
  userId: number,
  callback: () => void
): RealtimeChannel {
  return supabase
    .channel(`project_message_mention_badge_${buildingId}_${userId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "project_messages",
        filter: `building_id=eq.${buildingId}`,
      },
      callback
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "project_message_mentions",
        filter: `mentioned_user_id=eq.${userId}`,
      },
      callback
    )
    .subscribe();
}

export function subscribeToReactions(
  buildingProjectId: number,
  callback: () => void
): RealtimeChannel {
  // project_message_reactions has no building_project_id column, so the
  // server-side filter can't scope by channel. The callback simply reloads the
  // active channel's feed, so cross-channel reaction events are harmless noise
  // (a no-op refetch of what's already shown).
  return supabase
    .channel(`project_message_reactions_${buildingProjectId}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "project_message_reactions" },
      callback
    )
    .subscribe();
}
